import * as api from './api';

// Gate debug logging behind NODE_ENV for development use only
const debugLog = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

debugLog('[DEBUG dynamoDBService] Module loaded');

// Import sentiment types for review interface
export enum SentimentType {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
  NEUTRAL = 'NEUTRAL',
  MIXED = 'MIXED'
}

// Define the Review interface
export interface Review {
  id: string;
  name: string;
  date: string;
  rating: number;
  content: string;
  sentiment?: SentimentType;
  isPositive?: boolean;
}

// API response wrapper type (backend wraps all responses)
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// Sample reviews data for fallback when API is not available
export const sampleReviews: Review[] = [
  {
    id: '1',
    name: 'John Smith',
    date: 'August 15, 2025',
    rating: 5,
    content: 'Spitfire Catering made our wedding day absolutely perfect! The spit-braaied lamb was the highlight of our reception. All our guests were raving about the food, and the service was impeccable. Highly recommend!'
  },
  {
    id: '2',
    name: 'Sarah Johnson',
    date: 'July 28, 2025',
    rating: 5,
    content: 'We hired Spitfire for our company year-end function and they did not disappoint. The meat was tender and flavorful, and the setup added a great visual element to our event. The staff were professional and accommodating. Will definitely use them again!'
  },
  {
    id: '3',
    name: 'Michael Brown',
    date: 'July 10, 2025',
    rating: 4,
    content: 'Great food and service at our family reunion. The pork belly was a hit! Only reason for 4 stars instead of 5 is that they arrived a bit later than scheduled, but they made up for it with the quality of the food.'
  },
  {
    id: '4',
    name: 'Lisa Williams',
    date: 'June 22, 2025',
    rating: 5,
    content: 'Absolutely fantastic experience with Spitfire Catering! We had them for our son\'s 21st birthday party and everyone was impressed. The chicken was perfectly cooked and the vegetable skewers were a nice touch for our non-meat eating guests. The team was friendly and professional.'
  }
];

/**
 * Get all filtered reviews (public view - only positive, rating >= 3)
 * API endpoint: GET /reviews
 */
export const getAllReviews = async (): Promise<Review[]> => {
  debugLog('[DEBUG getAllReviews] Fetching filtered reviews');

  try {
    const response = await api.get<ApiResponse<Review[]>>('/reviews');
    const reviews = response.data || [];
    debugLog('[DEBUG getAllReviews] Retrieved', reviews.length, 'reviews');
    return reviews;
  } catch (error) {
    console.error('[DEBUG getAllReviews] Error fetching reviews:', error);
    debugLog('[DEBUG getAllReviews] Falling back to sample data');

    // Fallback to sample data
    const sampleReviewsWithSentiment = sampleReviews.map(review => ({
      ...review,
      sentiment: SentimentType.POSITIVE,
      isPositive: true
    }));

    // Filter and limit as per original logic
    const filteredReviews = sampleReviewsWithSentiment
      .filter(review => review.rating >= 3)
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 6);

    return filteredReviews;
  }
};

/**
 * Get a review by ID
 * API endpoint: GET /reviews/{id}
 */
export const getReviewById = async (id: string): Promise<Review | null> => {
  debugLog('[DEBUG getReviewById] Fetching review:', id);

  try {
    const response = await api.get<ApiResponse<Review>>(`/reviews/${id}`);
    const review = response.data;
    debugLog('[DEBUG getReviewById] Retrieved review:', id);
    return review;
  } catch (error) {
    console.error('[DEBUG getReviewById] Error fetching review:', error);

    // Fall back to sample data
    const review = sampleReviews.find(r => r.id === id);
    return review || null;
  }
};

/**
 * Add a new review
 * Sentiment analysis and ID generation handled by backend
 * API endpoint: POST /reviews
 */
export const addReview = async (
  review: Omit<Review, 'id' | 'sentiment' | 'isPositive'>
): Promise<Review> => {
  debugLog('[DEBUG addReview] Creating new review');

  try {
    const response = await api.post<ApiResponse<Review>>('/reviews', {
      name: review.name,
      date: review.date,
      rating: review.rating,
      content: review.content
    });

    const newReview = response.data;
    debugLog('[DEBUG addReview] Review created successfully:', newReview.id);
    return newReview;
  } catch (error) {
    console.error('[DEBUG addReview] Failed to add review:', error instanceof Error ? error.message : 'Unknown error');
    // Re-throw error so the caller can handle it properly
    throw error;
  }
};

/**
 * Delete a review (requires authentication)
 * API endpoint: DELETE /reviews/{id}
 */
export const deleteReview = async (id: string): Promise<void> => {
  debugLog('[DEBUG deleteReview] Deleting review:', id);

  try {
    await api.del(`/reviews/${id}`);
    debugLog('[DEBUG deleteReview] Review deleted successfully:', id);
  } catch (error) {
    console.error('[DEBUG deleteReview] Error deleting review:', error);
    throw error;
  }
};

/**
 * Update a review (requires authentication)
 * API endpoint: PUT /reviews/{id}
 */
export const updateReview = async (
  id: string,
  reviewData: Partial<Omit<Review, 'id'>>
): Promise<Review | null> => {
  debugLog('[DEBUG updateReview] Updating review:', id);

  try {
    const response = await api.put<ApiResponse<Review>>(`/reviews/${id}`, reviewData);
    const updatedReview = response.data;
    debugLog('[DEBUG updateReview] Review updated successfully:', id);
    return updatedReview;
  } catch (error) {
    console.error('[DEBUG updateReview] Error updating review:', error);
    return null;
  }
};

/**
 * Get all reviews without filtering (for admin use - requires authentication)
 * API endpoint: GET /reviews/all
 */
export const getAllReviewsUnfiltered = async (): Promise<Review[]> => {
  debugLog('[DEBUG getAllReviewsUnfiltered] Fetching all reviews (admin view)');

  try {
    const response = await api.get<ApiResponse<Review[]>>('/reviews/all');
    const reviews = response.data || [];
    debugLog('[DEBUG getAllReviewsUnfiltered] Retrieved', reviews.length, 'reviews');
    return reviews;
  } catch (error) {
    console.error('[DEBUG getAllReviewsUnfiltered] Error fetching reviews:', error);
    debugLog('[DEBUG getAllReviewsUnfiltered] Falling back to sample data');

    // Fallback to sample data
    const sampleReviewsWithSentiment = sampleReviews.map(review => ({
      ...review,
      sentiment: SentimentType.POSITIVE,
      isPositive: true
    }));

    return sampleReviewsWithSentiment;
  }
};
